/**
 * 미저장 입력 등록소 — **"지금 트리를 버리면 사용자가 잃는 것이 있는가".**
 *
 * ─── 왜 생겼나 (2026-09-03) ───
 * `app.tsx` 가 스스로 적어 둔 재검토 트리거가 걸렸다: **첫 뮤테이션 폼이 생기는 PR**
 * (직원 등록 다이얼로그, 2026-09-02). 그때까지 앱 루트의 401 정책은 한 갈래였다 —
 * 부트 상태를 `anonymous` 로 되돌린다. 그 한 줄이 라우터를 통째로 버리므로,
 * 사번·이름·이메일을 다 친 사람의 화면이 **말없이** 로그인 안내로 바뀐다.
 *
 * 더 나쁜 것은 바뀐 뒤에 뜨는 문장이다:
 *   "다시 로그인하면 하던 곳에서 이어서 진행할 수 있습니다."(`auth.unauthenticated.next`)
 * 입력은 이미 사라졌으니 그건 **거짓말**이었다. 이 파일은 그 문장을 참으로 만들려고 있다.
 *
 * ─── 왜 web-sdk 인가 ───
 * 등록하는 쪽은 **폼을 가진 화면 전부**이고 거기에는 vertical 화면이 포함된다.
 * `core-web` 에 두면 vertical 이 코어 구현을 import 해야 하고 `verticals-import-sdk-only`
 * 가 막는다. 사본을 만들면 컨텍스트가 둘이 되어 Provider 하나만 감싼 트리에서 **한쪽이
 * 조용히 아무도 안 듣는 등록을 받는다** — `SurfaceProvider` 를 여기 둔 논거 그대로다.
 * 정책(무엇을 할지)은 여전히 앱 루트가 소유한다. 여기 있는 것은 **사실 하나**뿐이다.
 *
 * ─── 왜 React 상태가 아니라 store 인가 ───
 * 판정하는 자리가 **RPC 이벤트 핸들러 안**이다. 그 핸들러는 `rpcEvents` 가 바뀔 때만 다시
 * 걸리므로(app.tsx), 리액트 상태를 읽으면 낡은 클로저를 본다. 매 렌더마다 구독을 다시 걸어
 * 피할 수도 있지만 그건 더 나쁘다 — 붙였다 떼는 **그 틈에 온 401 은 아무도 듣지 못하고**,
 * 그 실패는 재현되지 않는 형태로 나타난다. 그래서 값은 store 가 들고(`has()` 는 언제
 * 불러도 지금을 답한다), 리액트는 "0이 되는 순간"만 `subscribe` 로 듣는다.
 *
 * ─── 이것은 라우팅 가드가 아니다 ───
 * `beforeunload` 도, "정말 나가시겠습니까?" 도 여기서 하지 않는다. 그건 다른 결정이고
 * (사용자의 이동을 막는 일이다) 소비자도 아직 없다. 여기서 답하는 질문은 하나다:
 * **앱이 스스로 트리를 버리려 할 때, 버려도 되는가.**
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * 등록소. 구현이 아니라 **모양**이 계약이다 — 앱 루트는 `has()`·`subscribe()` 만 쓰고
 * 화면은 `hold()` 만 쓴다.
 */
export interface UnsavedWorkStore {
  /**
   * "나는 사용자가 잃으면 안 되는 입력을 들고 있다." 돌려주는 함수를 부를 때까지 유효하다.
   * 두 번 놓아도 안전하다 — 개수가 음수가 되면 `has()` 가 조용히 false 를 답하고,
   * 그 상태가 정확히 이 파일이 없애려는 버그다.
   */
  hold(): () => void;
  /** 지금 잃을 것이 있는가. **렌더 밖에서 불린다**(이벤트 핸들러). */
  has(): boolean;
  /** 개수가 바뀌면 알린다. 0 이 되는 순간을 기다리는 쪽이 있다(app.tsx 의 미뤄 둔 전환). */
  subscribe(listener: () => void): () => void;
}

export function createUnsavedWorkStore(): UnsavedWorkStore {
  /*
   * 숫자가 아니라 토큰 집합이다. 카운터로 두면 같은 폼이 두 번 놓았을 때 **남의 등록을**
   * 지운다 — 그러면 다른 폼의 입력이 사라지고, 원인과 증상이 서로 다른 화면에 있다.
   */
  const held = new Set<object>();
  const listeners = new Set<() => void>();

  const notify = () => {
    // 사본 위에서 돈다. 구독자가 구독 중에 해제하는 것이 정상 경로다(app.tsx 가 그렇게 한다).
    for (const fn of [...listeners]) fn();
  };

  return {
    hold() {
      const token = {};
      held.add(token);
      notify();
      return () => {
        if (!held.delete(token)) return;
        notify();
      };
    },
    has: () => held.size > 0,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const UnsavedWorkContext = createContext<UnsavedWorkStore | null>(null);

export function UnsavedWorkProvider({
  store,
  children,
}: {
  store: UnsavedWorkStore;
  children: ReactNode;
}) {
  return <UnsavedWorkContext.Provider value={store}>{children}</UnsavedWorkContext.Provider>;
}

/**
 * 이 컴포넌트가 미저장 입력을 들고 있는 동안 `active` 를 참으로 준다.
 *
 * ─── 기본값(no-op)을 두지 않는다 ───
 * Provider 밖에서 부르면 **던진다.** 조용한 no-op 은 "보호받고 있다"와 "아무도 안 듣는다"를
 * 구분 불가능하게 만들고, 그 두 상태의 차이가 정확히 사용자가 친 글자다 —
 * 이 파일이 존재하는 이유 자체가 위장되는 형태라 `usePorts`·`useSurfaceContext` 와 같은
 * 판단을 한다(기본값은 배선 사고를 정상 상태로 위장한다).
 */
export function useUnsavedWork(active: boolean): void {
  const store = useContext(UnsavedWorkContext);
  if (!store) {
    throw new Error(
      'UnsavedWorkProvider 밖에서 useUnsavedWork() 를 불렀다. 조용한 no-op 을 두지 않는 이유: ' +
        '"보호받고 있다"와 "아무도 안 듣는다"가 구분되지 않고, 그 차이는 401 이 오는 순간 ' +
        '사용자가 친 글자로 나타난다.',
    );
  }
  useEffect(() => {
    if (!active) return;
    return store.hold();
  }, [active, store]);
}

/**
 * 앱 루트가 자기 store 를 만드는 자리. `useState(createUnsavedWorkStore)` 로 한 번만 만든다 —
 * 렌더마다 새로 만들면 등록이 매번 새 store 로 가고 `has()` 는 영원히 false 다.
 */
export function useUnsavedWorkStore(): UnsavedWorkStore {
  return useState(createUnsavedWorkStore)[0];
}
